import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Starfield } from "./Starfield";
import { MaskLine } from "./Reveal";
import { HeroEmailForm } from "./EmailCapture";

const easeOutCubic = [0.33, 1, 0.68, 1];

const scrollToPricing = () => {
  const el = document.querySelector("#pricing");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yContent = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-6"
    >
      <Starfield />

      {/* Breathing glow */}
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        aria-hidden="true"
      >
        <div className="breathe h-[520px] w-[520px] rounded-full bg-[#2a1f4a] opacity-60 blur-[130px] mix-blend-screen sm:h-[640px] sm:w-[640px]" />
      </motion.div>
      <div
        className="pointer-events-none absolute left-1/2 top-[46%] h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#c9a84c]/10 breathe sm:h-[420px] sm:w-[420px]"
        aria-hidden="true"
      />

      {/* Vignette bottom for seamless transition */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent to-[#0a0e1a]" />

      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 mx-auto max-w-4xl text-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mb-8 text-xs font-semibold uppercase tracking-[0.4em] text-[#c9a84c] sm:text-sm"
        >
          Nuit Calme · 21 nuits
        </motion.p>

        <h1 className="text-5xl font-light leading-[1.05] tracking-tighter sm:text-6xl md:text-7xl">
          <MaskLine delay={0.5}>Retrouve des</MaskLine>
          <MaskLine delay={0.62} className="text-gold-gradient font-normal italic">
            soirées apaisées.
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: easeOutCubic }}
          className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
        >
          Un programme de 21 nuits pour décrocher, te détendre et retrouver un
          sommeil de qualité — sans écran, sans pression.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: easeOutCubic }}
          className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-cta"
            onClick={scrollToPricing}
            className="group inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-8 py-4 text-sm font-semibold text-[#0a0e1a] transition-colors duration-300 hover:bg-[#e8c97a] active:scale-[0.98]"
          >
            Découvrir le programme
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => document.querySelector("#program")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full border border-white/15 px-8 py-4 text-sm text-white/80 transition-colors duration-300 hover:border-white/40 hover:text-white"
          >
            Comment ça marche
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease: easeOutCubic }}
        >
          <HeroEmailForm />
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        style={{ opacity }}
        className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-10 w-6 items-start justify-center rounded-full border border-white/20 p-1.5">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-1.5 w-1 rounded-full bg-[#c9a84c]"
          />
        </div>
      </motion.div>
    </section>
  );
};
